import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, Progress, Textarea } from '@/components/ui'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatDateTime, formatDurationMs } from '@/lib/timeFormatters'
import { cn, getInitials } from '@/lib/utils'
import { isCancellableRunStatus, isWorkflowRunDetail, type WorkflowRun, type WorkflowRunApprovalRequest, type WorkflowRunDetail } from '@/types'
import { Check, RotateCw, X, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth, useSecurityReviewApprovalBlock } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { canManageWorkflowRun, ChainBadge, LinkedEntityBadge, WorkflowStatusBadge, workflowRunStatusProgressColors } from '../shared'

interface Props {
  run: WorkflowRun | WorkflowRunDetail
  onCancel: (runId: string) => void
  onRetry: (runId: string) => void
  onApprove: (runId: string, phaseId: string, comment?: string) => void
  onReject: (runId: string, phaseId: string, reason: string) => void
  canApprove: boolean
}

export function WorkflowRunRow({ run, onCancel, onRetry, onApprove, onReject, canApprove }: Props) {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { getProjectRoles, isAdmin, user, hasPermission } = useAuth(projectId)
  const [approveOpen, setApproveOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [approveComment, setApproveComment] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const effectiveStatus = run.status

  let pendingApproval: WorkflowRunApprovalRequest | undefined

  if (isWorkflowRunDetail(run)) {
    pendingApproval = run.approvalRequests.find(a => a.status === 'Pending')
  }

  const pendingApprovalPhaseId = pendingApproval?.phaseId ?? run.pendingApprovalPhaseId ?? undefined
  const pendingApprovalRoles = pendingApproval?.approvalRoles ?? run.pendingApprovalRoles ?? null

  const userProjectRoles = projectId ? getProjectRoles(projectId) : []
  const isRoleAuthorized = !pendingApprovalRoles || pendingApprovalRoles.length === 0
    || isAdmin
    || userProjectRoles.some(r => pendingApprovalRoles.includes(r))

  const securityReviewBlock = useSecurityReviewApprovalBlock(projectId ?? '', run.status === 'AwaitingApproval' ? run.id : null)

  const manageAllowed = canManageWorkflowRun(run, user?.id, isAdmin)
  const cancelAllowed = hasPermission(Permissions.Project.Workflow.Cancel, projectId) && manageAllowed
  const retryAllowed = hasPermission(Permissions.Project.Workflow.Trigger, projectId) && manageAllowed
  const canRetry = run.status === 'Cancelled' || run.status === 'Failed'
  const phaseProgress = run.totalPhases > 0 ? (run.completedPhases / run.totalPhases) * 100 : 0

  const handleRowClick = () => {
    navigate(`/projects/${projectId}/workflows/runs/${run.id}`)
  }

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation()
    action()
  }

  const handleApproveConfirm = () => {
    if (!pendingApprovalPhaseId) return
    onApprove(run.id, pendingApprovalPhaseId, approveComment.trim() || undefined)
    setApproveComment('')
    setApproveOpen(false)
  }

  const handleRejectConfirm = () => {
    if (!pendingApprovalPhaseId || !rejectReason.trim()) return
    onReject(run.id, pendingApprovalPhaseId, rejectReason.trim())
    setRejectReason('')
    setRejectOpen(false)
  }

  return (
    <TableRow
      className="cursor-pointer"
      onClick={handleRowClick}
      data-testid="workflow-run-row"
    >
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="min-w-0 font-medium">{run.definitionDisplayName}</span>
          <Badge variant="outline" className="text-xs shrink-0 border-border-accent">v{run.definitionVersion}</Badge>
        </div>
      </TableCell>

      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          <LinkedEntityBadge type={run.linkedEntityType} number={run.linkedEntityNumber} entityId={run.linkedEntityId} available={run.linkedEntityAvailable} status={run.linkedEntityStatus} />
          {run.chainRunId && (
            <span onClick={(e) => e.stopPropagation()}>
              <ChainBadge
                name={run.chainDisplayName ?? 'Chain'}
                onClick={() => navigate(`/projects/${projectId}/workflows/runs/chains/${run.chainRunId}`)}
              />
            </span>
          )}
        </div>
      </TableCell>

      <TableCell>
        <WorkflowStatusBadge status={effectiveStatus} />
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <Progress value={phaseProgress} className="h-2 flex-1" indicatorClassName={workflowRunStatusProgressColors[effectiveStatus]} />
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {run.completedPhases}/{run.totalPhases}
          </span>
        </div>
        {(run.status === 'Running' || effectiveStatus === 'AwaitingUserInput') && run.currentPhaseName && (
          <div className={cn('text-xs mt-1', effectiveStatus === 'AwaitingUserInput' ? 'text-warning-foreground' : 'text-info-foreground')}>
            {run.currentPhaseName}
          </div>
        )}
      </TableCell>

      <TableCell className="text-card-foreground">
        {formatDateTime(run.startedAt)}
      </TableCell>

      <TableCell className="text-card-foreground">
        {formatDurationMs(run.durationMs)}
      </TableCell>

      <TableCell className="text-card-foreground">
        {formatDurationMs(run.leadTimeMs)}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0 bg-slate-700">
            {getInitials(run.triggeredBy)}
          </div>
          <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
            <span className="min-w-0 text-sm font-medium">{run.triggeredBy}</span>
            {run.triggeredByEmail && (
              <span className="min-w-0 text-xs text-card-foreground">
                {run.triggeredByEmail}
              </span>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          {run.status === 'AwaitingApproval' && pendingApprovalPhaseId && canApprove && isRoleAuthorized && (
            <>
              <DropdownMenu open={approveOpen} onOpenChange={setApproveOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-success text-success-foreground border-success-foreground"
                    disabled={securityReviewBlock.disabled}
                    title={securityReviewBlock.reason}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Approve phase"
                  >
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Approve phase</p>
                  <Textarea
                    placeholder="Optional comment"
                    value={approveComment}
                    rows={3}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setApproveComment(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleApproveConfirm}>
                      Confirm Approve
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu open={rejectOpen} onOpenChange={setRejectOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-danger-foreground border-danger-foreground bg-danger"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Reject phase"
                  >
                    <X className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Reject phase</p>
                  <Textarea
                    placeholder="Required reason"
                    value={rejectReason}
                    rows={3}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" variant="destructive" onClick={handleRejectConfirm} disabled={!rejectReason.trim()}>
                      Confirm Reject
                    </Button>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {isCancellableRunStatus(run.status) && (
            <Button
              size="sm"
              variant="ghost"
              disabled={!cancelAllowed}
              title={cancelAllowed ? undefined : 'Only an administrator or the user who started the workflow can cancel it.'}
              onClick={(e) => handleActionClick(e, () => onCancel(run.id))}
              className="text-foreground"
            >
              <XCircle className="h-4 w-4 mr-1 text-accent-soft" /> Cancel
            </Button>
          )}
          {canRetry && (
            <Button
              size="sm"
              variant="ghost"
              className="text-foreground"
              disabled={!retryAllowed}
              title={retryAllowed
                ? 'Resume from where execution stopped — completed phases are preserved'
                : 'Only an administrator or the user who started the workflow can retry it.'}
              onClick={(e) => handleActionClick(e, () => onRetry(run.id))}
            >
              <RotateCw className="h-4 w-4 mr-1 text-accent-soft" /> Retry
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}
